import { Grid2 } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { FormikProps } from 'formik';
import { Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { object, string } from 'yup';

import ContentLoader from '@components/ContentLoader';
import { FormikDropdownField, FormikTextField } from '@components/FormikFields';
import RoundedButton from '@components/RoundedButton/index';

import { COUNTRIES } from '@constants/countries';
import { isDevelopment } from '@constants/env';
import { MANAGE_ACCOUNT } from '@constants/routes';

import { useUpdateAccountBillingAddress } from '@hooks/api/account';

import type { ManageAccountModel } from '@models/account';

const messages = defineMessages({
  header: {
    id: 'changeBillingAddress.header',
    defaultMessage: 'Your Payment Info',
  },
  address: {
    id: 'GLOBAL.address',
    defaultMessage: 'Address',
  },
  line2: {
    id: 'changeBillingAddress.line2',
    defaultMessage: 'Suite/Apartment',
  },
  city: {
    id: 'GLOBAL.city',
    defaultMessage: 'City',
  },
  region: {
    id: 'GLOBAL.region',
    defaultMessage: 'State/Province',
  },
  postalCode: {
    id: 'GLOBAL.postalCode',
    defaultMessage: 'Postal/Zip Code',
  },
  country: {
    id: 'GLOBAL.country',
    defaultMessage: 'Country',
  },
  pleaseSelect: {
    id: 'changeBillingAddress.pleaseSelect',
    defaultMessage: 'Please Select',
  },
  submit: {
    id: 'GLOBAL.submit',
    defaultMessage: 'Submit',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
});

type BillingAddressSchemaType = {
  line1: string;
  line2?: string;
  city: string;
  postal_code: string;
  state: string;
  country: string;
};

interface BillingAddressFormProps {
  account?: ManageAccountModel;
  loading: boolean;
}

const ChangeBillingAddressForm: React.FC<BillingAddressFormProps> = ({
  account,
  loading: baseLoading,
}) => {
  const { formatMessage } = useIntl();
  const { palette } = useTheme();
  const [updateBillingAddress, updateBillingAddressLoading] =
    useUpdateAccountBillingAddress();

  const loading = baseLoading || updateBillingAddressLoading;

  const initialValues: BillingAddressSchemaType = {
    line1: account?.billing_address ?? '',
    line2: account?.billing_address2 ?? '',
    city: account?.city ?? '',
    state: account?.region_id ?? '',
    postal_code: account?.zip_postal ?? '',
    country: account?.country_id ?? '',
  };

  const billingAddressSchema = object({
    line1: string().min(1).required(formatMessage(messages.required)),
    line2: string().optional(),
    city: string().min(1).required(formatMessage(messages.required)),
    state: string().min(1).required(formatMessage(messages.required)),
    postal_code: string().min(1).required(formatMessage(messages.required)),
    country: string().min(1).required(formatMessage(messages.required)),
  }).required(formatMessage(messages.required));

  async function handleSubmit(values: BillingAddressSchemaType) {
    if (!account?.account_id) {
      return;
    }

    try {
      await updateBillingAddress({
        input: {
          city: values.city,
          country: values.country,
          line1: values.line1,
          line2: values.line2,
          postal_code: values.postal_code,
          state: values.state,
          account_id: account.account_id,
        },
      });
    } catch (e) {
      if (isDevelopment) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    } finally {
      navigate(MANAGE_ACCOUNT);
    }
  }

  return (
    <ContentLoader loading={loading}>
      {account?.stripe_customer_id ? (
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={billingAddressSchema}
        >
          {({
            isSubmitting,
            setFieldValue,
            values,
          }: FormikProps<BillingAddressSchemaType>) => (
            <Form>
              <FormikTextField
                autoComplete="address-line1"
                name="line1"
                label={formatMessage(messages.address)}
              />

              <FormikTextField
                autoComplete="address-line2"
                name="line2"
                label={formatMessage(messages.line2)}
              />

              <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <FormikTextField
                    autoComplete="address-level2"
                    name="city"
                    label={formatMessage(messages.city)}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <FormikDropdownField
                    label={formatMessage(messages.region)}
                    labelColor={palette.primary.main}
                    name="state"
                    onChange={(value) => setFieldValue('state', value)}
                    options={COUNTRIES.find(
                      (c) => c.code === values.country || !values.country
                    )?.provinces.map((p) => ({
                      k: p,
                      v: p,
                    }))}
                  />
                </Grid2>

                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <FormikTextField
                    autoComplete="postal-code"
                    name="postal_code"
                    label={formatMessage(messages.postalCode)}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6 }}>
                  <FormikDropdownField
                    label={formatMessage(messages.country)}
                    labelColor={palette.primary.main}
                    name="country"
                    onChange={(value) => setFieldValue('country', value)}
                    options={COUNTRIES.map(({ code, name }) => ({
                      k: code,
                      v: name,
                    }))}
                  />
                </Grid2>
              </Grid2>

              <RoundedButton disabled={isSubmitting} type="submit">
                {formatMessage(messages.submit)}
              </RoundedButton>
            </Form>
          )}
        </Formik>
      ) : null}
    </ContentLoader>
  );
};

export default ChangeBillingAddressForm;
